import type { Expression, FuseOptionKey } from 'fuse.js';
import Fuse from 'fuse.js';
import { useCallback, useMemo } from 'react';

export type FuseSearchField<T> = {
  /** The field name on T to search */
  name: FuseOptionKey<T>;
  /** If true, uses exact-match prefix syntax ('query) instead of fuzzy */
  exact?: boolean;
};

export function parseQuery(query: string): { quoted: string[]; unquoted: string[] } {
  const quoted: string[] = [];
  const remaining = query
    .replace(/"([^"]+)"/g, (_, phrase: string) => {
      quoted.push(phrase);

      return '';
    })
    .replace(/"/g, '');
  const unquoted = remaining.split(' ').filter((q) => q !== '');

  return { quoted, unquoted };
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Generic Fuse.js search hook. Returns a filter function that tests whether
 * an item matches the given query across the configured fields.
 *
 * Supports quoted phrases for word-boundary exact matching (e.g. `"VS"` won't
 * match "VSOP"). Unquoted terms use Fuse.js fuzzy/extended search.
 *
 * @param query - The search string (space-separated terms are ANDed, "quoted" terms use exact match)
 * @param items - The list of items to index
 * @param fields - The fields to search, with exact vs fuzzy per field
 * @param getItemKey - Returns a unique key for each item (used for the match set)
 */
export function useFuseSearch<T>(
  query: string | undefined,
  items: T[],
  fields: FuseSearchField<T>[],
  getItemKey: (item: T) => string | number
): (item: T) => boolean {
  const keys = useMemo(() => fields.map((f) => f.name), [fields]);

  const index = useMemo(() => {
    return new Fuse<T>(items, {
      threshold: 0,
      ignoreLocation: true,
      useExtendedSearch: true,
      keys,
    });
  }, [items, keys]);

  const matchingKeys = useMemo(() => {
    if (!query || query === '') {
      return new Set(items.map(getItemKey));
    }

    const { quoted, unquoted } = parseQuery(query);

    function orExpression(q: string): Expression {
      return {
        $or: fields.map((f) => ({
          [f.name as string]: f.exact ? `'${q}` : q,
        })),
      };
    }

    const allTerms = [...unquoted, ...quoted];
    if (allTerms.length === 0) {
      return new Set(items.map(getItemKey));
    }

    const results = index.search({
      $and: allTerms.map(orExpression),
    });

    if (quoted.length === 0) {
      return new Set(results.map((result) => getItemKey(result.item)));
    }

    // Post-filter: quoted phrases must appear as whole words in at least one field
    const fieldNames = fields.map((f) => f.name as string);

    return new Set(
      results
        .filter((result) =>
          quoted.every((phrase) => {
            const regex = new RegExp(`\\b${escapeRegExp(phrase)}\\b`, 'i');

            return fieldNames.some((key) =>
              regex.test(String((result.item as Record<string, unknown>)[key] ?? ''))
            );
          })
        )
        .map((result) => getItemKey(result.item))
    );
  }, [query, index, items, fields, getItemKey]);

  return useCallback((item: T) => matchingKeys.has(getItemKey(item)), [matchingKeys, getItemKey]);
}
